import { Link } from "react-router-dom";

export default function JointMobilityReport() {
  return (
    <div className="font-['Lato'] bg-background-50 text-foreground-900">
      {/* Download PDF Button */}
      <div className="fixed bottom-8 right-8 z-50 no-print">
        <Link
          to="/the-joint-mobility-report/pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-accent-500 hover:bg-accent-600 text-white px-6 py-3 rounded-full shadow-lg font-semibold text-sm flex items-center gap-2 whitespace-nowrap cursor-pointer transition-colors"
        >
          <i className="ri-download-line text-lg" />
          Download PDF
        </Link>
      </div>
      <style>{`@media print { .no-print { display: none !important; } }`}</style>
      {/* ================================================================
          PAGE 1 — COVER
          ================================================================ */}
      <section className="relative w-full h-screen min-h-[700px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Mature%20couple%20in%20their%2050s%20walking%20together%20outdoors%20on%20a%20scenic%20autumn%20trail%20with%20golden%20hour%20sunlight%2C%20warm%20earthy%20tones%2C%20editorial%20magazine%20photography%20style%2C%20soft%20natural%20light%2C%20elegant%20and%20sophisticated%20composition%2C%20shallow%20depth%20of%20field%2C%20trees%20with%20amber%20leaves%20in%20background%2C%20active%20healthy%20lifestyle%20portrait%2C%20premium%20lifestyle%20photography&width=1920&height=1080&seq=ebook-cover-2026&orientation=landscape"
            alt="Mature couple walking together on a scenic autumn trail"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-amber-900/25 to-black/35" />
        </div>

        <div className="relative z-10 text-center px-6 md:px-12 max-w-3xl mx-auto">
          <div className="mb-12">
            <p className="font-['Playfair_Display'] text-accent-400 text-base md:text-lg tracking-[0.3em] uppercase mb-8">
              Premium Health Editorial
            </p>
            <h1 className="font-['Playfair_Display'] text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] mb-6 tracking-tight">
              THE JOINT
              <br />
              MOBILITY
              <br />
              REPORT
            </h1>
            <div className="w-24 h-px bg-accent-400 mx-auto my-10" />
            <p className="font-['Lato'] text-white/85 text-lg md:text-xl font-light leading-relaxed max-w-xl mx-auto">
              What Every Adult Over 50 Should Know
              <br />
              About Maintaining Comfortable Movement
            </p>
          </div>

          <div className="absolute bottom-12 left-0 right-0 text-center">
            <p className="font-['Playfair_Display'] text-accent-300 text-sm tracking-[0.2em] uppercase">
              Premium Health Editorial &nbsp;·&nbsp; 2026 Edition
            </p>
          </div>
        </div>
      </section>

      {/* ================================================================
          PAGE 2 — LETTER FROM THE EDITOR
          ================================================================ */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="mb-16">
            <p className="font-['Playfair_Display'] text-accent-600 text-sm tracking-[0.25em] uppercase mb-4">
              Letter from the Editor
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-10">
              Why Joint Health Matters
              <br />
              More Than Ever After 50
            </h2>
            <div className="w-16 h-px bg-accent-400 mb-10" />
          </div>

          <div className="space-y-7 text-foreground-700 text-base md:text-lg leading-relaxed">
            <p>
              <span className="font-['Playfair_Display'] text-5xl text-accent-500 leading-none float-left mr-4 mt-1">A</span>
              s we move through life, our bodies tell a story—one of resilience, adaptation, and change. After the age of 50, that story takes on a new chapter, and for many of us, the narrative includes questions about joint comfort and mobility that we never had to ask before.
            </p>
            <p>
              This report was created not to sell a solution, but to provide clarity. In an era of overwhelming health information, we believe that understanding the <strong className="text-foreground-900">science behind how our joints age</strong> is the most powerful tool anyone can have.
            </p>
            <p>
              What you will find in these pages is a carefully researched exploration of the seven most common—and often hidden—habits that influence joint health after 50. Each section draws on published medical research and clinical insights, translated into language that is clear, practical, and actionable.
            </p>
            <p>
              We believe that informed choices lead to better outcomes. Whether you are experiencing occasional stiffness or simply want to maintain your active lifestyle for decades to come, knowledge is the foundation upon which every healthy habit is built.
            </p>
            <p className="text-foreground-600 text-base">
              With warmth and respect,
              <br />
              <span className="font-['Playfair_Display'] text-accent-600 text-lg italic">The Editorial Team</span>
            </p>
          </div>

          {/* Pull Quote */}
          <div className="mt-16 border-l-[3px] border-accent-400 pl-8 py-2">
            <blockquote className="font-['Playfair_Display'] text-2xl md:text-3xl italic text-accent-700 leading-relaxed">
              "Mobility is not simply about movement.
              <br />
              It is about preserving the freedom
              <br />
              to live life on your own terms."
            </blockquote>
          </div>

          <div className="mt-10 text-foreground-500 text-sm tracking-[0.15em] uppercase">
            The Joint Mobility Report &nbsp;·&nbsp; 2026 Edition
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-background-300" />
          <i className="ri-star-fill text-accent-300 text-xs" />
          <div className="flex-1 h-px bg-background-300" />
        </div>
      </div>

      {/* ================================================================
          PAGE 3 — THE SILENT SHIFT
          ================================================================ */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <p className="font-['Playfair_Display'] text-accent-600 text-sm tracking-[0.25em] uppercase mb-4">
            Chapter One
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-10">
            Why Joints Feel Different
            <br />
            As We Age
          </h2>
          <div className="w-16 h-px bg-accent-400 mb-10" />

          <p className="text-foreground-700 text-base md:text-lg leading-relaxed mb-12">
            The human body is remarkably adaptive, but after age 50, several physiological changes begin to quietly reshape how our joints feel and function. Understanding these changes is the first step toward responding to them wisely.
          </p>

          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
            <div className="bg-background-100/70 border border-background-200/70 rounded-lg p-6">
              <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-full mb-4">
                <i className="ri-shield-line text-accent-600 text-lg" />
              </div>
              <h3 className="font-['Playfair_Display'] text-lg font-semibold text-foreground-900 mb-2">Cartilage Changes</h3>
              <p className="text-foreground-600 text-sm leading-relaxed">
                The smooth, cushioning tissue at the ends of bones gradually becomes thinner and less resilient. This natural wear can reduce the shock-absorbing capacity of your joints over time.
              </p>
            </div>
            <div className="bg-background-100/70 border border-background-200/70 rounded-lg p-6">
              <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-full mb-4">
                <i className="ri-drop-line text-primary-600 text-lg" />
              </div>
              <h3 className="font-['Playfair_Display'] text-lg font-semibold text-foreground-900 mb-2">Joint Lubrication</h3>
              <p className="text-foreground-600 text-sm leading-relaxed">
                Synovial fluid, the body's natural joint lubricant, decreases in both quantity and quality with age. This can lead to increased friction and that familiar morning stiffness.
              </p>
            </div>
            <div className="bg-background-100/70 border border-background-200/70 rounded-lg p-6">
              <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-full mb-4">
                <i className="ri-body-scan-line text-secondary-600 text-lg" />
              </div>
              <h3 className="font-['Playfair_Display'] text-lg font-semibold text-foreground-900 mb-2">Muscle Loss</h3>
              <p className="text-foreground-600 text-sm leading-relaxed">
                Starting around age 40, adults lose approximately 3-5% of muscle mass per decade. Weaker muscles provide less support to joints, transferring more stress directly to cartilage and bone.
              </p>
            </div>
            <div className="bg-background-100/70 border border-background-200/70 rounded-lg p-6">
              <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-full mb-4">
                <i className="ri-time-line text-accent-600 text-lg" />
              </div>
              <h3 className="font-['Playfair_Display'] text-lg font-semibold text-foreground-900 mb-2">Recovery Changes</h3>
              <p className="text-foreground-600 text-sm leading-relaxed">
                The body's repair mechanisms slow with age. Inflammation lingers longer, and tissue regeneration becomes less efficient, meaning that recovery from activity takes more time than it once did.
              </p>
            </div>
          </div>

          {/* Timeline */}
          <h3 className="font-['Playfair_Display'] text-xl font-semibold text-foreground-900 text-center mb-8">
            A Timeline of Joint Aging
          </h3>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute top-6 left-0 right-0 h-0.5 bg-background-300 hidden md:block" />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-4">
              {[
                { age: "20s–30s", label: "Peak Years", desc: "Cartilage is thick and resilient. Joint fluid is plentiful. Recovery after activity is fast and efficient." },
                { age: "40s", label: "Early Changes", desc: "Subtle thinning of cartilage begins. Muscle mass starts to decline gradually. Joints may occasionally feel stiff after inactivity." },
                { age: "50s", label: "Noticeable Shift", desc: "Changes become more apparent. Morning stiffness may linger longer. Joint lubrication decreases more rapidly." },
                { age: "60+", label: "Adaptation Phase", desc: "The body requires more intentional care. Proactive habits become essential for maintaining comfort and mobility." },
              ].map((item) => (
                <div key={item.age} className="relative text-center pt-14">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-accent-500 text-white flex items-center justify-center font-['Playfair_Display'] text-sm font-bold z-10">
                    {item.age.split('–')[0]}
                  </div>
                  <p className="font-['Playfair_Display'] text-lg font-semibold text-foreground-900 mb-1">{item.age}</p>
                  <p className="text-accent-600 text-xs font-semibold tracking-wider uppercase mb-2">{item.label}</p>
                  <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-background-300" />
          <span className="font-['Playfair_Display'] text-xs tracking-[0.2em] uppercase text-foreground-400">The Seven Hidden Habits</span>
          <div className="flex-1 h-px bg-background-300" />
        </div>
      </div>

      {/* ================================================================
          PAGE 4 — MISTAKE #1: Sedentary Lifestyle
          ================================================================ */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <span className="font-['Playfair_Display'] text-5xl font-bold text-accent-300">01</span>
            <div>
              <p className="text-accent-600 text-xs tracking-[0.2em] uppercase">Hidden Habit</p>
            </div>
          </div>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-8">
            Living More Sedentary
            <br />
            Than You Realize
          </h2>

          <div className="rounded-xl overflow-hidden mb-10">
            <img
              src="https://readdy.ai/api/search-image?query=Older%20adult%20sitting%20on%20a%20comfortable%20sofa%20in%20a%20well%20lit%20living%20room%2C%20subtle%20depiction%20of%20sedentary%20indoor%20lifestyle%2C%20warm%20natural%20light%20streaming%20through%20window%2C%20editorial%20magazine%20photography%2C%20soft%20neutral%20color%20palette%20with%20cream%20and%20beige%20tones%2C%20elegant%20composition%2C%20thoughtful%20mood%2C%20premium%20lifestyle%20editorial%20style&width=1200&height=700&seq=ebook-sedentary-2026&orientation=landscape"
              alt="Thoughtful adult in a well-lit living room representing modern sedentary lifestyle"
              className="w-full h-auto object-cover"
            />
          </div>

          <div className="space-y-5 text-foreground-700 text-base md:text-lg leading-relaxed mb-10">
            <p>
              Many adults over 50 are far less active than they believe. Modern conveniences—remote controls, online shopping, food delivery apps, and streaming entertainment—have engineered movement out of daily life. What feels like a comfortable routine may actually be accelerating joint stiffness.
            </p>
            <p>
              Research shows that <strong className="text-foreground-900">prolonged sitting reduces the production of synovial fluid</strong>, the body's natural joint lubricant. Without regular movement to distribute this fluid throughout the joint capsule, stiffness becomes a self-reinforcing cycle.
            </p>
          </div>

          {/* Expert Insight */}
          <div className="bg-accent-50/60 border-l-[3px] border-accent-400 p-6 rounded-r-lg mb-8">
            <p className="font-['Playfair_Display'] text-sm font-semibold text-accent-700 uppercase tracking-wider mb-2">Expert Insight</p>
            <p className="text-foreground-700 text-sm leading-relaxed italic">
              "Motion is lotion for the joints. Even a five-minute walk every hour can significantly improve the distribution of nutrients within joint cartilage. The body was designed to move—frequently and throughout the day."
            </p>
          </div>

          {/* Action Steps */}
          <div className="bg-background-100/70 border border-background-200/70 rounded-lg p-6">
            <p className="font-['Playfair_Display'] text-sm font-semibold text-foreground-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <i className="ri-check-double-line text-accent-500" />
              Action Steps
            </p>
            <ul className="space-y-2 text-foreground-700 text-sm">
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-line text-accent-500 mt-0.5 shrink-0" />
                <span>Set a reminder to stand and stretch every 45 minutes during the day</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-line text-accent-500 mt-0.5 shrink-0" />
                <span>Incorporate a daily 20-minute walk—outdoors when possible for added benefit</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-line text-accent-500 mt-0.5 shrink-0" />
                <span>Replace 30 minutes of evening screen time with gentle stretching or mobility exercises</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ================================================================
          PAGE 5 — MISTAKE #2: Carrying Extra Weight
          ================================================================ */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-background-100/50">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <span className="font-['Playfair_Display'] text-5xl font-bold text-accent-300">02</span>
            <div>
              <p className="text-accent-600 text-xs tracking-[0.2em] uppercase">Hidden Habit</p>
            </div>
          </div>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-8">
            Carrying Extra Weight
          </h2>

          <div className="rounded-xl overflow-hidden mb-10">
            <img
              src="https://readdy.ai/api/search-image?query=Close%20up%20of%20a%20digital%20bathroom%20scale%20on%20a%20warm%20wooden%20floor%20with%20soft%20morning%20light%2C%20minimalist%20wellness%20concept%2C%20clean%20aesthetic%2C%20cream%20and%20beige%20color%20palette%2C%20editorial%20magazine%20photography%2C%20soft%20shadows%2C%20elegant%20and%20sophisticated%20composition%2C%20subtle%20health%20awareness%20theme&width=1200&height=700&seq=ebook-weight-2026&orientation=landscape"
              alt="Minimalist wellness concept with warm natural light representing healthy weight awareness"
              className="w-full h-auto object-cover"
            />
          </div>

          <div className="space-y-5 text-foreground-700 text-base md:text-lg leading-relaxed mb-10">
            <p>
              Weight management becomes increasingly important after 50—not just for heart health and diabetes prevention, but directly for the health of your joints. Extra body weight places additional mechanical stress on weight-bearing joints, particularly the knees, hips, and lower spine.
            </p>
            <p>
              The numbers are striking: research indicates that <strong className="text-foreground-900">every pound of body weight exerts approximately four pounds of pressure</strong> on the knee joint during walking. Over the course of a day, this cumulative load can accelerate cartilage wear significantly.
            </p>
          </div>

          {/* Statistics Box */}
          <div className="bg-primary-50/60 border border-primary-200/60 rounded-lg p-6 mb-8">
            <div className="flex items-center gap-4 mb-3">
              <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-full">
                <i className="ri-bar-chart-box-line text-primary-600 text-xl" />
              </div>
              <p className="font-['Playfair_Display'] text-lg font-semibold text-foreground-900">The Numbers Speak Clearly</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div>
                <p className="font-['Playfair_Display'] text-3xl font-bold text-primary-600">4x</p>
                <p className="text-foreground-600 text-xs mt-1">Pressure multiplier on knees per extra pound when walking</p>
              </div>
              <div>
                <p className="font-['Playfair_Display'] text-3xl font-bold text-primary-600">30%</p>
                <p className="text-foreground-600 text-xs mt-1">Approximate reduction in knee stress with just 10 lbs of weight loss</p>
              </div>
              <div>
                <p className="font-['Playfair_Display'] text-3xl font-bold text-primary-600">50+</p>
                <p className="text-foreground-600 text-xs mt-1">Age when joint-friendly weight management becomes most impactful</p>
              </div>
            </div>
          </div>

          <p className="text-foreground-600 text-sm leading-relaxed italic">
            The encouraging news is that even modest weight loss can yield meaningful reductions in joint stress. It is not about perfection—it is about progress.
          </p>
        </div>
      </section>

      {/* ================================================================
          PAGE 6 — MISTAKE #3: Ignoring Early Warning Signs
          ================================================================ */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <span className="font-['Playfair_Display'] text-5xl font-bold text-accent-300">03</span>
            <div>
              <p className="text-accent-600 text-xs tracking-[0.2em] uppercase">Hidden Habit</p>
            </div>
          </div>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-8">
            Ignoring Early Warning Signs
          </h2>

          <p className="text-foreground-700 text-base md:text-lg leading-relaxed mb-10">
            Many adults adopt a "wait and see" approach to joint discomfort. While occasional stiffness is normal, your body often sends signals long before a problem becomes serious. Learning to recognize and respond to these signals can make a significant difference in long-term joint health.
          </p>

          {/* Warning Signs */}
          <div className="space-y-5">
            {[
              {
                icon: "ri-sun-line",
                title: "Morning Stiffness",
                desc: "Waking up with stiff joints that take more than 30 minutes to loosen may indicate reduced synovial fluid quality. While occasional stiffness is normal, persistent patterns deserve attention.",
                color: "bg-amber-100",
                iconColor: "text-amber-600",
              },
              {
                icon: "ri-run-line",
                title: "Reduced Flexibility",
                desc: "Noticing that everyday movements—bending to tie shoes, reaching for items on a high shelf, turning to look behind you—have become more difficult is a sign that joint range of motion may be decreasing.",
                color: "bg-orange-100",
                iconColor: "text-orange-600",
              },
              {
                icon: "ri-alert-line",
                title: "Persistent Discomfort",
                desc: "Discomfort that lingers beyond 48 hours after normal activity, or that appears without an obvious cause, is the body's way of requesting attention. Early intervention is almost always simpler than late-stage treatment.",
                color: "bg-red-100",
                iconColor: "text-red-600",
              },
            ].map((item, idx) => (
              <div key={idx} className="flex gap-4 items-start">
                <div className={`w-10 h-10 flex items-center justify-center ${item.color} rounded-full shrink-0`}>
                  <i className={`${item.icon} ${item.iconColor} text-lg`} />
                </div>
                <div>
                  <h3 className="font-['Playfair_Display'] text-lg font-semibold text-foreground-900 mb-1">{item.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================
          PAGE 7 — MISTAKE #4: Inflammatory Lifestyle
          ================================================================ */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-background-100/50">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <span className="font-['Playfair_Display'] text-5xl font-bold text-accent-300">04</span>
            <div>
              <p className="text-accent-600 text-xs tracking-[0.2em] uppercase">Hidden Habit</p>
            </div>
          </div>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-8">
            Following an Inflammatory Lifestyle
          </h2>

          <div className="rounded-xl overflow-hidden mb-10">
            <img
              src="https://readdy.ai/api/search-image?query=Beautiful%20flat%20lay%20of%20colorful%20fresh%20anti-inflammatory%20foods%20including%20berries%20salmon%20leafy%20greens%20nuts%20and%20olive%20oil%20on%20a%20warm%20wooden%20table%2C%20natural%20sunlight%20from%20window%2C%20editorial%20food%20photography%2C%20warm%20earthy%20tones%2C%20elegant%20composition%2C%20premium%20magazine%20style%2C%20soft%20shadows&width=1200&height=700&seq=ebook-food-2026&orientation=landscape"
              alt="Colorful anti-inflammatory foods on wooden table in natural light"
              className="w-full h-auto object-cover"
            />
          </div>

          <p className="text-foreground-700 text-base md:text-lg leading-relaxed mb-10">
            What you eat directly impacts the level of inflammation in your body—and your joints feel the effects. After 50, the body becomes more sensitive to dietary choices that may have been tolerated more easily in younger years.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Foods To Limit */}
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-close-circle-line text-red-500 text-lg" />
                <h3 className="font-['Playfair_Display'] text-lg font-semibold text-foreground-900">Foods To Limit</h3>
              </div>
              <ul className="space-y-3">
                {[
                  "Refined sugars and sweetened beverages",
                  "Highly processed snack foods",
                  "Excessive red and processed meats",
                  "Fried foods and trans fats",
                  "Refined carbohydrates (white bread, pastries)",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-foreground-600 text-sm">
                    <i className="ri-subtract-line text-red-400 mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Foods To Embrace */}
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-check-line text-green-600 text-lg" />
                <h3 className="font-['Playfair_Display'] text-lg font-semibold text-foreground-900">Foods To Embrace</h3>
              </div>
              <ul className="space-y-3">
                {[
                  "Fatty fish rich in omega-3 (salmon, sardines)",
                  "Colorful berries and dark leafy greens",
                  "Nuts, seeds, and extra virgin olive oil",
                  "Turmeric, ginger, and anti-inflammatory spices",
                  "Whole grains and legumes for sustained energy",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-foreground-600 text-sm">
                    <i className="ri-add-line text-green-500 mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-foreground-500 text-xs mt-6 italic">
            This information is educational in nature and is not a substitute for personalized dietary advice from a qualified professional.
          </p>
        </div>
      </section>

      {/* ================================================================
          PAGE 8 — MISTAKE #5: Losing Muscle Strength
          ================================================================ */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <span className="font-['Playfair_Display'] text-5xl font-bold text-accent-300">05</span>
            <div>
              <p className="text-accent-600 text-xs tracking-[0.2em] uppercase">Hidden Habit</p>
            </div>
          </div>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-8">
            Losing Muscle Strength
            <br />
            With Age
          </h2>

          <div className="rounded-xl overflow-hidden mb-10">
            <img
              src="https://readdy.ai/api/search-image?query=Mature%20woman%20in%20her%2050s%20doing%20gentle%20resistance%20training%20with%20light%20dumbbells%20in%20a%20bright%20airy%20home%20space%2C%20warm%20natural%20light%2C%20active%20aging%20lifestyle%2C%20editorial%20magazine%20photography%2C%20cream%20and%20warm%20neutral%20tones%2C%20elegant%20composition%2C%20confident%20and%20healthy%20expression%2C%20premium%20wellness%20editorial&width=1200&height=700&seq=ebook-active-aging-2026&orientation=landscape"
              alt="Mature woman doing gentle resistance training in bright home space"
              className="w-full h-auto object-cover"
            />
          </div>

          <div className="space-y-5 text-foreground-700 text-base md:text-lg leading-relaxed mb-10">
            <p>
              Sarcopenia—the age-related loss of muscle mass and strength—is one of the most significant, yet most overlooked, contributors to joint problems after 50. Muscles are the primary support system for your joints. When they weaken, your joints absorb more impact with every step.
            </p>
            <p>
              The good news is that <strong className="text-foreground-900">muscle tissue remains highly responsive to stimulus at any age</strong>. Research has demonstrated that adults in their 70s and 80s can build meaningful muscle strength with consistent, appropriate training.
            </p>
          </div>

          {/* Did You Know Box */}
          <div className="bg-secondary-50/60 border border-secondary-200/60 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-full">
                <i className="ri-lightbulb-line text-secondary-600 text-lg" />
              </div>
              <p className="font-['Playfair_Display'] text-lg font-semibold text-foreground-900">Did You Know?</p>
            </div>
            <p className="text-foreground-600 text-sm leading-relaxed">
              Strength training just twice per week has been shown to improve joint stability, reduce discomfort, and increase functional mobility in adults over 50. The key is consistency, not intensity. Body-weight exercises, resistance bands, and light dumbbells are all effective tools.
            </p>
          </div>
        </div>
      </section>

      {/* ================================================================
          PAGE 9 — MISTAKE #6: Underestimating Sleep
          ================================================================ */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-background-100/50">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <span className="font-['Playfair_Display'] text-5xl font-bold text-accent-300">06</span>
            <div>
              <p className="text-accent-600 text-xs tracking-[0.2em] uppercase">Hidden Habit</p>
            </div>
          </div>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-8">
            Underestimating The
            <br />
            Power Of Sleep
          </h2>

          <div className="rounded-xl overflow-hidden mb-10">
            <img
              src="https://readdy.ai/api/search-image?query=Premium%20bedroom%20with%20crisp%20white%20linen%20bedding%2C%20soft%20warm%20morning%20light%20streaming%20through%20sheer%20curtains%2C%20elegant%20minimalist%20decor%2C%20peaceful%20serene%20atmosphere%2C%20editorial%20interior%20photography%2C%20cream%20and%20warm%20beige%20palette%2C%20magazine%20quality%2C%20soft%20textures%2C%20restful%20sleep%20concept&width=1200&height=700&seq=ebook-sleep-2026&orientation=landscape"
              alt="Peaceful premium bedroom with warm morning light and elegant decor"
              className="w-full h-auto object-cover"
            />
          </div>

          {/* Pull Quote */}
          <div className="border-l-[3px] border-accent-400 pl-8 py-3 mb-10">
            <blockquote className="font-['Playfair_Display'] text-2xl md:text-3xl italic text-accent-700 leading-relaxed">
              Recovery Happens
              <br />
              While You Sleep
            </blockquote>
          </div>

          <div className="space-y-5 text-foreground-700 text-base md:text-lg leading-relaxed">
            <p>
              Sleep is when the body performs its most critical repair work. During deep sleep, the body releases growth hormone, which stimulates tissue repair throughout the body—including in joint cartilage. Chronic sleep deprivation creates a repair deficit that accumulates over time.
            </p>
            <p>
              Research indicates that adults who consistently sleep fewer than six hours per night report <strong className="text-foreground-900">significantly higher rates of joint discomfort</strong> compared to those who achieve seven to eight hours of quality rest.
            </p>
            <p>
              Beyond duration, sleep quality matters deeply. Fragmented sleep, even if it totals eight hours, does not provide the same restorative benefit as uninterrupted rest.
            </p>
          </div>
        </div>
      </section>

      {/* ================================================================
          PAGE 10 — MISTAKE #7: Self-Medicating
          ================================================================ */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <span className="font-['Playfair_Display'] text-5xl font-bold text-accent-300">07</span>
            <div>
              <p className="text-accent-600 text-xs tracking-[0.2em] uppercase">Hidden Habit</p>
            </div>
          </div>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-8">
            Self-Medicating Instead
            <br />
            Of Investigating
          </h2>

          <div className="rounded-xl overflow-hidden mb-10">
            <img
              src="https://readdy.ai/api/search-image?query=Mature%20doctor%20having%20a%20warm%20consultation%20with%20an%20older%20patient%20in%20a%20bright%20modern%20medical%20office%2C%20professional%20healthcare%20setting%2C%20natural%20light%2C%20trusting%20and%20compassionate%20atmosphere%2C%20editorial%20photography%2C%20warm%20neutral%20tones%2C%20elegant%20composition%2C%20premium%20healthcare%20editorial%20style%2C%20no%20fear%20based%20imagery&width=1200&height=700&seq=ebook-doctor-2026&orientation=landscape"
              alt="Doctor having warm consultation with older patient in bright medical office"
              className="w-full h-auto object-cover"
            />
          </div>

          <div className="space-y-5 text-foreground-700 text-base md:text-lg leading-relaxed">
            <p>
              When joint discomfort appears, the most common response is to reach for an over-the-counter remedy and carry on. While occasional use of such products may be appropriate, using them to mask symptoms without investigating their cause is a pattern that can lead to more significant issues over time.
            </p>
            <p>
              <strong className="text-foreground-900">Pain is a signal, not the problem itself.</strong> Addressing the signal without understanding what triggered it is like turning off a fire alarm without checking for smoke. The underlying issue remains—and often worsens.
            </p>
            <p>
              A proactive approach involves consulting with a healthcare professional who can assess joint function, mobility patterns, and contributing lifestyle factors. This is not about fear—it is about empowerment. Understanding the root cause of discomfort opens the door to addressing it effectively.
            </p>
          </div>

          <div className="bg-background-100/70 border border-background-200/70 rounded-lg p-6 mt-10">
            <p className="font-['Playfair_Display'] text-sm font-semibold text-foreground-900 uppercase tracking-wider mb-2">A Thoughtful Approach</p>
            <p className="text-foreground-600 text-sm leading-relaxed">
              Regular check-ups, open conversations with your doctor about joint health, and a willingness to explore lifestyle-based solutions before relying on medication are hallmarks of a proactive, empowered approach to aging well.
            </p>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-background-300" />
          <i className="ri-check-double-line text-accent-400 text-sm" />
          <div className="flex-1 h-px bg-background-300" />
        </div>
      </div>

      {/* ================================================================
          PAGE 11 — THE JOINT HEALTH CHECKLIST
          ================================================================ */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <p className="font-['Playfair_Display'] text-accent-600 text-sm tracking-[0.25em] uppercase mb-4 text-center">
            Your Personal Guide
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4 text-center">
            The Joint Health
            <br />
            Checklist
          </h2>
          <p className="text-foreground-500 text-sm text-center mb-14">
            A printable daily and weekly guide to maintaining comfortable, confident movement
          </p>

          <div className="bg-background-50 border-2 border-background-200/70 rounded-xl p-8 md:p-10">
            <div className="grid grid-cols-1 gap-5">
              {[
                { icon: "ri-walk-line", title: "Daily Movement", desc: "Aim for at least 30 minutes of low-impact activity every day. Walking, swimming, cycling, and gentle yoga all qualify." },
                { icon: "ri-drop-line", title: "Hydration", desc: "Drink water consistently throughout the day. Joint cartilage is approximately 80% water—adequate hydration supports its shock-absorbing function." },
                { icon: "ri-moon-line", title: "Sleep", desc: "Prioritize 7–8 hours of quality, uninterrupted sleep each night. This is when your body repairs joint tissue and regulates inflammation." },
                { icon: "ri-heart-pulse-line", title: "Strength Training", desc: "Include resistance exercises at least twice per week. Strong muscles protect joints by absorbing impact and improving stability." },
                { icon: "ri-scales-line", title: "Healthy Weight", desc: "Maintain a weight that supports your joints. Even modest weight loss can significantly reduce stress on knees and hips." },
                { icon: "ri-bowl-line", title: "Balanced Nutrition", desc: "Emphasize anti-inflammatory foods—omega-3 rich fish, colorful vegetables, nuts, seeds, and whole grains. Limit processed foods and added sugars." },
                { icon: "ri-user-heart-line", title: "Professional Guidance", desc: "Schedule regular check-ups and discuss any joint concerns with your healthcare provider. Early intervention is almost always simpler and more effective." },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-4 p-4 rounded-lg hover:bg-background-100/60 transition-colors">
                  <div className="w-11 h-11 flex items-center justify-center bg-accent-100 rounded-full shrink-0">
                    <i className={`${item.icon} text-accent-600 text-lg`} />
                  </div>
                  <div>
                    <h3 className="font-['Playfair_Display'] text-lg font-semibold text-foreground-900 mb-1">{item.title}</h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-center text-foreground-400 text-xs mt-6 italic">
            Print this checklist and keep it somewhere visible as a daily reminder.
          </p>
        </div>
      </section>

      {/* ================================================================
          PAGE 12 — KEY TAKEAWAYS
          ================================================================ */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Mature%20couple%20walking%20toward%20the%20camera%20on%20a%20scenic%20trail%20with%20warm%20golden%20sunset%20light%2C%20confident%20happy%20expressions%2C%20active%20healthy%20aging%20lifestyle%2C%20editorial%20magazine%20photography%2C%20warm%20earthy%20golden%20tones%2C%20elegant%20composition%2C%20premium%20lifestyle%20editorial%20quality%2C%20soft%20natural%20backlight&width=1920&height=1080&seq=ebook-closing-2026&orientation=landscape"
            alt="Mature couple walking toward the camera with golden sunset light"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="font-['Playfair_Display'] text-accent-300 text-sm tracking-[0.25em] uppercase mb-6">
            Closing Thoughts
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-10">
            Healthy Movement Is Built
            <br />
            One Day At A Time
          </h2>

          <div className="w-16 h-px bg-accent-400 mx-auto mb-10" />

          <div className="space-y-6 text-white/80 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-12">
            <p>
              Joint health after 50 is not about a single decision or a dramatic transformation. It is about the accumulation of small, consistent choices made day after day. A short walk. A glass of water. An extra hour of sleep. A conversation with your doctor.
            </p>
            <p>
              These micro-decisions compound over time, shaping how you move through your 60s, 70s, and beyond. The body you inhabit tomorrow is being built by the choices you make today.
            </p>
          </div>

          {/* Closing Quote */}
          <div className="border-l-[3px] border-accent-400 pl-8 py-3 inline-block text-left max-w-xl">
            <blockquote className="font-['Playfair_Display'] text-2xl md:text-3xl italic text-white leading-relaxed">
              "The goal is not simply to add
              <br />
              years to life. The goal is to
              <br />
              add life to those years."
            </blockquote>
          </div>

          <div className="mt-16 pt-10 border-t border-white/15">
            <p className="font-['Playfair_Display'] text-accent-300 text-sm tracking-[0.15em] uppercase">
              The Joint Mobility Report
            </p>
            <p className="text-white/50 text-xs mt-2 tracking-[0.1em] uppercase">
              Premium Health Editorial &nbsp;·&nbsp; 2026 Edition
            </p>
          </div>
        </div>
      </section>

      {/* Footer with Privacy Policy and Terms links */}
      <footer className="bg-foreground-950 text-white/50 py-8 px-6">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <span className="font-['Playfair_Display'] text-white font-semibold">
            JOINT GENESIS
          </span>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-white transition-colors">
              Terms of Use
            </Link>
          </div>
          <span>&copy; 2026 All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}